import React from 'react';
import ContentfulRow from 'components/Contentful/ContentfulRow';
import Styles from 'components/Styles';
import BackgroundImage from 'components/Core/BackgroundImage';
import { usePromoBanner } from 'hooks/useHeaderHeight';
import Breadcrumbs from 'components/Core/Breadcrumbs';
import useGenerateBreadcrumbs from 'utils/getBreadcrumbData';
import { useCheckHeroDark } from 'utils/colorUtility';
import CarouselBackground from './CarouselBackground';
import findFirstRichTextColor from './utils/findFirstRichTextColor';
import {
  InnerContainerCol,
  SectionContainerRow,
  SectionContainer,
  SectionInnerContainer,
} from './styles';

const ContentfulSection = ({
  anchorId,
  className,
  contentful_id: contentfulId,
  fullWidthBackground,
  isHero,
  rows,
  spacing,
  standardInnerContainerWidth,
  styles,
  variation,
  seo,
  ...otherProps
}) => {
  const {
    backgroundColor,
    customBackgroundDesktop,
    customBackgroundMobile,
    desktopCarouselBackgroundImages,
    mobileCarouselBackgroundImages,
    hideOnDesktop,
    hideOnMobile,
    marginDesktop,
    marginMobile,
    paddingDesktop,
    paddingMobile,
    bottomGradient,
  } = styles || {};
  const hasCarouselImages = desktopCarouselBackgroundImages || mobileCarouselBackgroundImages;
  const mobileDisplay = hideOnMobile ? 'none' : 'inline-flex';
  const desktopDisplay = hideOnDesktop ? 'none' : 'inline-flex';
  const { backgroundImage, ...otherStyles } = styles || {};
  const promoHeight = usePromoBanner(true);

  const generatedBreadcrumbs = useGenerateBreadcrumbs(seo, styles);
  const firstRichTextColor = findFirstRichTextColor(rows);
  const isHeroDark = useCheckHeroDark({ styles, textColor: firstRichTextColor });
  const breadcrumbs = {
    data: otherProps.breadcrumbs?.data || generatedBreadcrumbs,
    theme: otherProps.breadcrumbs?.theme || (isHeroDark ? 'light' : 'dark'),
    layout: otherProps.breadcrumbs?.layout || '',
  };
  const showBreadcrumbs = breadcrumbs.data?.length > 0;

  return (
    <SectionContainer
      id={contentfulId}
      $isHero={isHero}
      className={`section-container ${isHero ? 'hero-section-container' : ''} ${className || ''}`}
    >
      {backgroundImage && <BackgroundImage image={backgroundImage} />}
      <Styles {...otherStyles} backgroundColor={fullWidthBackground ? backgroundColor : null}>
        {hasCarouselImages && (
          <CarouselBackground
            bottomGradient={bottomGradient}
            mobileImages={mobileCarouselBackgroundImages}
            desktopImages={desktopCarouselBackgroundImages}
            desktopBackgroundStyles={styles?.customBackgroundDesktop}
            mobileBackgroundStyles={styles?.customBackgroundMobile}
            variation={variation}
          />
        )}
        <SectionInnerContainer
          className={`section-inner-container ${isHero ? 'hero-section-inner-container' : ''}`}
          $isHero={isHero}
          $showBreadcrumbs={showBreadcrumbs}
        >
          {showBreadcrumbs && <Breadcrumbs {...breadcrumbs} />}
          <SectionContainerRow
            $marginDesktop={marginDesktop}
            $marginMobile={marginMobile}
            $paddingMobile={paddingMobile}
            $paddingDesktop={paddingDesktop}
            $displayMobile={mobileDisplay}
            $displayDesktop={desktopDisplay}
            $isHero={isHero}
            $spacing={spacing}
            $fullWidth={!standardInnerContainerWidth}
            $fullWidthBackground={fullWidthBackground}
            $promoHeight={promoHeight}
            justify="center"
            align="middle"
            type="flex"
            className={`section-container-row ${isHero ? 'hero-section-container-row' : ''}`}
            id={anchorId}
          >
            <InnerContainerCol
              $backgroundColor={backgroundColor}
              $customBackgroundDesktop={customBackgroundDesktop}
              $customBackgroundMobile={customBackgroundMobile}
              $fullWidthBackground={fullWidthBackground}
              $fullWidth={!standardInnerContainerWidth}
              xs={24}
              className="inner-container-col"
            >
              {rows?.map((row, index) => (
                <ContentfulRow key={index} parentBackgroundColor={backgroundColor} {...row} />
              ))}
            </InnerContainerCol>
          </SectionContainerRow>
        </SectionInnerContainer>
      </Styles>
    </SectionContainer>
  );
};

export default ContentfulSection;
